import * as THREE from "three";
import { WORLD, paletteAt, heightT } from "./config.js";

/**
 * Lay works out on a descending helix around the trunk, grouped by year
 * (reverse chronological, matching the Eleventy sort).
 *
 * Returns entries of { work, position, angle, groupY } plus year groups.
 */
export function layoutWorks(works) {
  // Group consecutive works by year (input is already sorted desc).
  const groups = [];
  for (const w of works) {
    const y = String(w.year || "");
    const last = groups[groups.length - 1];
    if (last && last.year === y) last.items.push(w);
    else groups.push({ year: y, items: [w] });
  }

  const span = WORLD.worksTop - WORLD.worksBottom;
  const GROUP_GAP = 1.6; // extra breathing room between year clusters
  const totalGaps = GROUP_GAP * Math.max(0, groups.length - 1);
  const perWork = (span - totalGaps) / Math.max(1, works.length);

  const entries = [];
  const yearMarkers = [];

  let cursor = WORLD.worksTop;
  let angle = Math.PI * 0.35;

  for (const group of groups) {
    yearMarkers.push({ year: group.year, y: cursor - perWork * 0.5 });
    for (const w of group.items) {
      const y = cursor - perWork * 0.5;
      const r = WORLD.workRadius + Math.sin(entries.length * 2.4) * 0.55;
      const pos = new THREE.Vector3(Math.cos(angle) * r, y, Math.sin(angle) * r);
      entries.push({ work: w, position: pos, angle, y });
      cursor -= perWork;
      angle += WORLD.helixStep;
    }
    cursor -= GROUP_GAP;
  }

  return { entries, yearMarkers };
}

/* ---------- halo sprite texture (canvas-generated radial glow) ---------- */

function makeHaloTexture() {
  const c = document.createElement("canvas");
  c.width = c.height = 128;
  const ctx = c.getContext("2d");
  const g = ctx.createRadialGradient(64, 64, 2, 64, 64, 64);
  g.addColorStop(0, "rgba(255,255,255,0.85)");
  g.addColorStop(0.25, "rgba(255,255,255,0.28)");
  g.addColorStop(0.6, "rgba(255,255,255,0.07)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 128);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/* ---------- orbiting particles (single draw call for all works) ---------- */

const ORBIT_VERT = /* glsl */ `
  uniform float uTime;
  uniform float uPixelRatio;
  uniform float uHover; // hovered work index, -1 when none

  attribute vec3 aCenter;
  attribute float aPhase;
  attribute float aRadius;
  attribute float aSpeed;
  attribute float aTilt;
  attribute float aWork;
  attribute vec3 aColor;

  varying vec3 vColor;
  varying float vGlow;

  void main() {
    float t = uTime * aSpeed + aPhase;

    // Orbit in a tilted plane around the work's centre.
    vec3 u = vec3(cos(aTilt), 0.0, sin(aTilt));
    vec3 v = normalize(cross(u, vec3(sin(aTilt) * 0.4, 1.0, 0.0)));

    float hovered = step(abs(aWork - uHover), 0.25);
    float r = aRadius * (1.0 - hovered * 0.35); // particles gather when hovered

    vec3 p = aCenter + u * cos(t) * r + v * sin(t) * r;

    vec4 mv = modelViewMatrix * vec4(p, 1.0);
    float twinkle = 0.7 + 0.3 * sin(uTime * 2.3 + aPhase * 17.0);
    float size = (1.3 + hovered * 1.1) * twinkle;
    gl_PointSize = size * uPixelRatio * (120.0 / -mv.z);
    gl_PointSize = min(gl_PointSize, 14.0 * uPixelRatio);

    vColor = aColor * (1.0 + hovered * 0.8);
    vGlow = twinkle;
    gl_Position = projectionMatrix * mv;
  }
`;

const ORBIT_FRAG = /* glsl */ `
  precision highp float;
  varying vec3 vColor;
  varying float vGlow;

  void main() {
    vec2 uv = gl_PointCoord - 0.5;
    float d = length(uv);
    float disc = smoothstep(0.5, 0.05, d);
    gl_FragColor = vec4(vColor, disc * disc * vGlow * 0.55);
  }
`;

/* ---------- tether strands: pulses flowing tree -> artwork ---------- */

const STRAND_VERT = /* glsl */ `
  uniform float uTime;
  uniform float uPixelRatio;

  attribute float aAlong;  // 0 at branch, 1 at artwork
  attribute float aSeed;
  attribute vec3 aColor;

  varying vec3 vColor;
  varying float vPulse;

  void main() {
    vec3 p = position;
    p.x += sin(uTime * 0.8 + aSeed * 20.0) * 0.03;
    p.y += cos(uTime * 0.6 + aSeed * 14.0) * 0.03;

    vec4 mv = modelViewMatrix * vec4(p, 1.0);

    float w = fract(aAlong - uTime * 0.25 + aSeed * 0.05);
    float pulse = smoothstep(0.12, 0.0, w);
    vPulse = pulse;

    float size = 0.9 + pulse * 2.2;
    gl_PointSize = size * uPixelRatio * (120.0 / -mv.z);
    gl_PointSize = min(gl_PointSize, 12.0 * uPixelRatio);

    vColor = aColor;
    gl_Position = projectionMatrix * mv;
  }
`;

const STRAND_FRAG = /* glsl */ `
  precision highp float;
  varying vec3 vColor;
  varying float vPulse;

  void main() {
    vec2 uv = gl_PointCoord - 0.5;
    float d = length(uv);
    float disc = smoothstep(0.5, 0.05, d);
    vec3 col = vColor * (0.5 + vPulse * 2.2);
    gl_FragColor = vec4(col, disc * (0.25 + vPulse * 0.75));
  }
`;

/**
 * Build everything artwork-related.
 *
 * @param {Array} entries from layoutWorks()
 * @param {(target:THREE.Vector3)=>THREE.Vector3} anchorFor tree anchor lookup
 */
export function buildWorks({ entries, anchorFor, orbitersPerWork = 120 }) {
  const group = new THREE.Group();
  const halo = makeHaloTexture();
  const loader = new THREE.TextureLoader();

  const BASE_SIZE = 2.35;

  const items = entries.map((entry, index) => {
    const [r, g, b] = paletteAt(heightT(entry.y));
    const tint = new THREE.Color(r, g, b);

    const node = new THREE.Group();
    node.position.copy(entry.position);

    // Face outward from the trunk (billboarded softly toward camera later).
    node.lookAt(
      entry.position.x * 2,
      entry.position.y,
      entry.position.z * 2
    );

    // Placeholder material until the texture streams in.
    const mat = new THREE.MeshBasicMaterial({
      color: new THREE.Color(0.06, 0.075, 0.14),
      transparent: true,
      opacity: 0.92,
      side: THREE.DoubleSide,
      toneMapped: false
    });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    mesh.scale.set(BASE_SIZE, BASE_SIZE, 1);
    mesh.userData.workIndex = index;
    node.add(mesh);

    // Luminous halo behind the plane.
    const haloMat = new THREE.SpriteMaterial({
      map: halo,
      color: tint,
      transparent: true,
      opacity: 0.2,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
    const haloSprite = new THREE.Sprite(haloMat);
    haloSprite.scale.set(BASE_SIZE * 2.6, BASE_SIZE * 2.6, 1);
    haloSprite.position.z = -0.18;
    node.add(haloSprite);

    group.add(node);

    return {
      entry,
      node,
      mesh,
      mat,
      haloMat,
      tint,
      textureState: "idle", // idle | loading | ready
      hover: 0
    };
  });

  /* --- orbiters (one geometry) --- */
  const orbCount = items.length * orbitersPerWork;
  const orbPos = new Float32Array(orbCount * 3);
  const orbCenter = new Float32Array(orbCount * 3);
  const orbPhase = new Float32Array(orbCount);
  const orbRadius = new Float32Array(orbCount);
  const orbSpeed = new Float32Array(orbCount);
  const orbTilt = new Float32Array(orbCount);
  const orbWork = new Float32Array(orbCount);
  const orbColor = new Float32Array(orbCount * 3);

  let oi = 0;
  for (let wi = 0; wi < items.length; wi++) {
    const it = items[wi];
    for (let k = 0; k < orbitersPerWork; k++, oi++) {
      orbCenter[oi * 3] = it.entry.position.x;
      orbCenter[oi * 3 + 1] = it.entry.position.y;
      orbCenter[oi * 3 + 2] = it.entry.position.z;
      orbPhase[oi] = Math.random() * Math.PI * 2;
      orbRadius[oi] = 1.7 + Math.random() * 1.5;
      orbSpeed[oi] = 0.25 + Math.random() * 0.45;
      orbTilt[oi] = Math.random() * Math.PI;
      orbWork[oi] = wi;
      orbColor[oi * 3] = it.tint.r;
      orbColor[oi * 3 + 1] = it.tint.g;
      orbColor[oi * 3 + 2] = it.tint.b;
    }
  }

  const orbGeo = new THREE.BufferGeometry();
  orbGeo.setAttribute("position", new THREE.BufferAttribute(orbPos, 3));
  orbGeo.setAttribute("aCenter", new THREE.BufferAttribute(orbCenter, 3));
  orbGeo.setAttribute("aPhase", new THREE.BufferAttribute(orbPhase, 1));
  orbGeo.setAttribute("aRadius", new THREE.BufferAttribute(orbRadius, 1));
  orbGeo.setAttribute("aSpeed", new THREE.BufferAttribute(orbSpeed, 1));
  orbGeo.setAttribute("aTilt", new THREE.BufferAttribute(orbTilt, 1));
  orbGeo.setAttribute("aWork", new THREE.BufferAttribute(orbWork, 1));
  orbGeo.setAttribute("aColor", new THREE.BufferAttribute(orbColor, 3));

  const orbMat = new THREE.ShaderMaterial({
    vertexShader: ORBIT_VERT,
    fragmentShader: ORBIT_FRAG,
    uniforms: {
      uTime: { value: 0 },
      uPixelRatio: { value: Math.min(window.devicePixelRatio || 1, 2) },
      uHover: { value: -1 }
    },
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending
  });
  const orbiters = new THREE.Points(orbGeo, orbMat);
  orbiters.frustumCulled = false;
  group.add(orbiters);

  /* --- tether strands --- */
  const PER_STRAND = 34;
  const strandCount = items.length * PER_STRAND;
  const stPos = new Float32Array(strandCount * 3);
  const stAlong = new Float32Array(strandCount);
  const stSeed = new Float32Array(strandCount);
  const stColor = new Float32Array(strandCount * 3);

  const a = new THREE.Vector3();
  const c = new THREE.Vector3();
  const p = new THREE.Vector3();

  let si = 0;
  for (const it of items) {
    a.copy(anchorFor(it.entry.position));
    // Control point bows the strand outward/downward for an organic drape.
    c.lerpVectors(a, it.entry.position, 0.5);
    c.y -= 0.9;
    const seed = Math.random();
    for (let k = 0; k < PER_STRAND; k++, si++) {
      const t = (k + Math.random() * 0.7) / PER_STRAND;
      // Quadratic bezier.
      const it1 = 1 - t;
      p.set(0, 0, 0)
        .addScaledVector(a, it1 * it1)
        .addScaledVector(c, 2 * it1 * t)
        .addScaledVector(it.entry.position, t * t);
      stPos[si * 3] = p.x + (Math.random() - 0.5) * 0.06;
      stPos[si * 3 + 1] = p.y + (Math.random() - 0.5) * 0.06;
      stPos[si * 3 + 2] = p.z + (Math.random() - 0.5) * 0.06;
      stAlong[si] = t;
      stSeed[si] = seed;
      stColor[si * 3] = it.tint.r;
      stColor[si * 3 + 1] = it.tint.g;
      stColor[si * 3 + 2] = it.tint.b;
    }
  }

  const stGeo = new THREE.BufferGeometry();
  stGeo.setAttribute("position", new THREE.BufferAttribute(stPos, 3));
  stGeo.setAttribute("aAlong", new THREE.BufferAttribute(stAlong, 1));
  stGeo.setAttribute("aSeed", new THREE.BufferAttribute(stSeed, 1));
  stGeo.setAttribute("aColor", new THREE.BufferAttribute(stColor, 3));

  const stMat = new THREE.ShaderMaterial({
    vertexShader: STRAND_VERT,
    fragmentShader: STRAND_FRAG,
    uniforms: {
      uTime: { value: 0 },
      uPixelRatio: { value: Math.min(window.devicePixelRatio || 1, 2) }
    },
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending
  });
  const strands = new THREE.Points(stGeo, stMat);
  strands.frustumCulled = false;
  group.add(strands);

  /* --- texture streaming --- */

  // Downscaled variants generated by scripts/build-textures.mjs; the
  // originals (some are 4K PNGs) are far too heavy to decode mid-scroll.
  function textureUrlFor(image) {
    return (
      "/assets/tex/" +
      image
        .replace(/^\/assets\//, "")
        .replace(/\//g, "__")
        .replace(/\.[a-z0-9]+$/i, "") +
      ".jpg"
    );
  }

  function loadTexture(it, useOriginal = false) {
    if (it.textureState !== "idle" || !it.entry.work.image) return;
    it.textureState = "loading";
    loader.load(
      useOriginal ? it.entry.work.image : textureUrlFor(it.entry.work.image),
      (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.anisotropy = 4;
        it.mat.map = tex;
        // Slightly dimmed at rest so bright images sit under the bloom
        // threshold; hover/detail brightens to full.
        it.mat.color.setScalar(0.62);
        it.mat.needsUpdate = true;
        // Fit the plane to the image's aspect within the base footprint.
        const iw = tex.image?.width || 1;
        const ih = tex.image?.height || 1;
        const aspect = iw / ih;
        if (aspect >= 1) {
          it.mesh.scale.set(BASE_SIZE, BASE_SIZE / aspect, 1);
        } else {
          it.mesh.scale.set(BASE_SIZE * aspect, BASE_SIZE, 1);
        }
        it.textureState = "ready";
      },
      undefined,
      () => {
        if (!useOriginal) {
          // Variant missing (e.g. not rebuilt yet): fall back to the
          // original image.
          it.textureState = "idle";
          loadTexture(it, true);
        } else {
          it.textureState = "ready"; // keep the placeholder tint on failure
        }
      }
    );
  }

  const meshes = items.map((it) => it.mesh);

  /**
   * Per-frame update.
   * @param {number} t seconds
   * @param {number} focusY camera focus height (streams nearby textures)
   * @param {number} hoverIndex hovered work (-1 none)
   * @param {number} dt delta seconds
   */
  function update(t, focusY, hoverIndex, dt) {
    orbMat.uniforms.uTime.value = t;
    orbMat.uniforms.uHover.value = hoverIndex;
    stMat.uniforms.uTime.value = t;

    for (let i = 0; i < items.length; i++) {
      const it = items[i];

      // Stream textures for anything within ~1.5 screens of the camera.
      if (Math.abs(it.entry.y - focusY) < 14) loadTexture(it);

      // Hover ease.
      const target = i === hoverIndex ? 1 : 0;
      it.hover += (target - it.hover) * Math.min(1, dt * 8);

      const s = 1 + it.hover * 0.12;
      it.node.scale.setScalar(s);
      it.haloMat.opacity = 0.2 + it.hover * 0.3;
      if (it.textureState === "ready" && it.mat.map) {
        // Cap below the bloom threshold so bright images never blow out.
        it.mat.color.setScalar(0.62 + it.hover * 0.08);
      }

      // Slow bob so nothing feels frozen.
      it.node.position.y = it.entry.position.y + Math.sin(t * 0.5 + i * 1.7) * 0.08;
    }
  }

  return { group, items, meshes, update };
}
